import { Dataset } from '@api/customTypes';
import { EditIcon, LogsIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { Group } from '@components/layout';
import { ExternalLink } from '@components/link';
import { useModal } from '@components/modal';
import { Typography } from '@components/typography';
import { formatSizeFromBytes } from '@helper-functions/formatSize';
import { Stack } from '@mui/material';
import { useOrganizationContext } from '@navigation/OrganizationContext';
import {
  DatasetsDetailsEditModal,
  DatasetsDetailsInfoPreview,
  useDatasets,
} from '@subsets/workspaces';
import { navigate } from 'gatsby';

interface DatasetsDetailsInfoProps {
  workspaceId: string;
  dataset: Dataset;
}

export const DatasetsDetailsInfo = ({
  workspaceId,
  dataset: { datasetId, description, files, name, size, source, tags, type },
}: DatasetsDetailsInfoProps) => {
  const { organizationId } = useOrganizationContext();
  const navigateToSnapshotGraph = () =>
    navigate(`/${organizationId}/workspaces/${workspaceId}/graphs/${source?.graphId}/`);
  const { refetchDatasets, setSelectedDataset } = useDatasets();

  const { showModal } = useModal({
    component: DatasetsDetailsEditModal,
    componentProps: {
      workspaceId,
      detailName: name,
      detailDescription: description || '',
      detailTags: tags || [],
      datasetId,
      refetchDatasets,
    },
    modalProps: { title: 'Edit Dataset' },
  });

  return (
    <Stack>
      <Group alignItems="center" justifyContent="space-between">
        <Typography handleOverFlow variant="h3" data-cy="Datasets-Details-Info-Dataset-Name">
          {name}
        </Typography>
        <Group gap={1}>
          <ExternalLink
            unstyled
            href={`/${organizationId}/workspaces/${workspaceId}/datasets/${datasetId}/logs`}
          >
            <IconButton tooltip="View logs" Icon={LogsIcon} size={18} />
          </ExternalLink>
          <IconButton onClick={() => showModal()} Icon={EditIcon} size={18} />
        </Group>
      </Group>
      <Typography variant="caption2">{description || 'No description'}</Typography>
      <Group flexWrap="wrap" alignItems="center" gap={1} my={4}>
        {type === 'synthetic' && (
          <>
            <Typography variant="body2">{source?.channel}</Typography>
            <Typography variant="caption2">•</Typography>
            <Typography variant="body2">{source?.runs} runs</Typography>
            <Typography variant="caption2">•</Typography>
          </>
        )}
        <Typography variant="body2">
          {formatSizeFromBytes(Number(size) || 0)} ({files} images)
        </Typography>
      </Group>
      <DatasetsDetailsInfoPreview name="Workspace Id" value={workspaceId} />
      <DatasetsDetailsInfoPreview name="Dataset Id" value={datasetId} />
      {type === 'synthetic' && (
        <>
          <DatasetsDetailsInfoPreview
            name="Channel"
            value={source?.channelId}
            display={source?.channel}
          />
          <DatasetsDetailsInfoPreview
            name="Graph"
            title="Go to graph"
            display={source?.graph}
            value={source?.graphId}
            onClick={source?.graphId && navigateToSnapshotGraph}
          />
        </>
      )}
      {(type === 'synthetic' || type === 'mixed') && (
        <DatasetsDetailsInfoPreview name="Seed" value={source?.seed} />
      )}
      {type === 'gan' && (
        <>
          <DatasetsDetailsInfoPreview
            name="GAN model"
            value={source?.modelId}
            display={source?.model}
          />
          <DatasetsDetailsInfoPreview
            name="Source dataset"
            value={source?.datasetId}
            display={source?.dataset}
            title="Go to dataset"
            onClick={() => source?.datasetId && setSelectedDataset(source?.datasetId)}
          />
        </>
      )}
    </Stack>
  );
};
